import type { MessageDto } from '@planuze/admin-contracts';
import { create } from 'zustand';

/* Estado do chat de um ticket (28-14). Mensagem otimista ganha `pending: true`; ao
   chegar o echo do servidor (o DO ecoa de volta ao remetente com `clientMessageId`,
   ADR-0434), removemos a pendente correspondente — match por id de correlação, com
   fallback por conteúdo quando o eco não traz o id. Sem persistência — reset ao trocar
   de ticket. */
export interface ChatMessage extends MessageDto {
  readonly pending?: boolean;
}

interface ChatState {
  readonly messages: readonly ChatMessage[];
  readonly typingSenders: readonly string[];
  readonly userOnline: boolean;
  readonly setHistory: (messages: readonly MessageDto[]) => void;
  readonly addMessage: (message: MessageDto, clientMessageId?: string) => void;
  readonly addOptimistic: (message: ChatMessage) => void;
  readonly setTyping: (senderId: string, isTyping: boolean) => void;
  readonly setPresence: (userOnline: boolean) => void;
  readonly reset: () => void;
}

export const useChatStore = create<ChatState>((set) => ({
  messages: [],
  typingSenders: [],
  userOnline: false,
  setHistory: (messages) => set({ messages: [...messages] }),
  addMessage: (message, clientMessageId) =>
    set((state) => {
      /* Reconciliação otimista (ADR-0434): prefere o `clientMessageId` ecoado (id estável
         da bolha do remetente); só cai no match por conteúdo do staff quando o eco não
         traz o id. */
      const withoutPending =
        clientMessageId !== undefined
          ? state.messages.filter((m) => m.id !== clientMessageId)
          : message.senderType === 'staff'
            ? state.messages.filter((m) => !(m.pending === true && m.content === message.content))
            : state.messages;
      if (withoutPending.some((m) => m.id === message.id)) return { messages: withoutPending };
      return { messages: [...withoutPending, message] };
    }),
  addOptimistic: (message) => set((state) => ({ messages: [...state.messages, message] })),
  setTyping: (senderId, isTyping) =>
    set((state) => {
      const others = state.typingSenders.filter((id) => id !== senderId);
      return { typingSenders: isTyping ? [...others, senderId] : others };
    }),
  setPresence: (userOnline) => set({ userOnline }),
  reset: () => set({ messages: [], typingSenders: [], userOnline: false }),
}));
